import {
  useBoundProp,
  useCardSession,
  useCardStatus,
  useCardSessionControls,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';
import { addDays } from 'date-fns/addDays';
import { format } from 'date-fns/format';
import { isSameDay } from 'date-fns/isSameDay';
import { startOfDay } from 'date-fns/startOfDay';
import { useState, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Calendar } from '@/components/ui/calendar';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { TimePickerPopover } from '@/components/ui/TimePickerPopover';
import { cn } from '@/lib/utils';
import { formatDateWithDay } from '@/utils/date';

import { FieldError, PillField } from '../field';
import { fieldErrorId, useElementId, useFieldChecks } from '../fieldIds';
import { CardTime as Time } from '../internals/CardTime';
import { DateRangePicker } from '../internals/DateRangePicker';
import {
  dayLabel,
  isDateOnly,
  parseDateValue,
  shiftEnd,
  withDayOf,
  withTimeOf,
  writeDateValue,
} from '../internals/dateValue';
import { useCardMetrics } from '../internals/useCardMetrics';
import {
  HELD_CLASS,
  ICON_CLASS,
  PILL_CLASS,
  PILL_ERROR_CLASS,
  sizingClass,
  TEXT_STYLE,
  TONE_CLASS,
} from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';

type RangeEnd = 'start' | 'end';

const TEXT_CLASS = `inline-flex min-w-0 flex-wrap items-center gap-x-[calc(6*var(--ac-u))] text-foreground ${TEXT_STYLE.body}`;

const TEXT_TRIGGER_CLASS =
  'mx-[calc(-6*var(--ac-u))] rounded-md px-[calc(6*var(--ac-u))] text-left transition-colors duration-[120ms] hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strongest motion-reduce:duration-[60ms]';

const END_BUTTON_CLASS =
  'inline-flex h-6 items-center rounded-md border border-border px-2 text-xs text-foreground transition-colors duration-[120ms] hover:bg-surface-hover aria-pressed:border-border-strongest aria-pressed:bg-surface-active motion-reduce:duration-[60ms]';

const Punctuation = ({ text }: { text: string }) => (
  <span aria-hidden="true" className="text-foreground-muted">
    {text}
  </span>
);

const RangeText = ({
  start,
  end,
  allDay,
}: {
  start: Date;
  end: Date | null;
  allDay: boolean;
}) => {
  const oneDay = end === null || isSameDay(start, end);
  if (allDay) {
    return (
      <>
        <span>{formatDateWithDay(start)}</span>
        {oneDay || !end ? null : (
          <>
            <Punctuation text="–" />
            <span>{formatDateWithDay(end)}</span>
          </>
        )}
      </>
    );
  }
  const closesStartDay =
    end !== null && end.getTime() === addDays(startOfDay(start), 1).getTime();
  if (oneDay || closesStartDay) {
    return (
      <>
        <span>{formatDateWithDay(start)}</span>
        <Punctuation text="·" />
        <Time date={start} />
        {end ? (
          <>
            <Punctuation text="–" />
            <Time date={end} />
          </>
        ) : null}
      </>
    );
  }
  return (
    <>
      <span>{formatDateWithDay(start)}</span>
      <Time date={start} />
      <Punctuation text="–" />
      <span>{formatDateWithDay(end)}</span>
      <Time date={end} />
    </>
  );
};

// The old range, struck; one the client cannot read shows as the server wrote it
const PreviousRange = ({
  start,
  end,
  allDay,
}: {
  start: string;
  end: string | undefined;
  allDay: boolean;
}) => {
  const startDate = parseDateValue(start);
  return (
    <>
      <s className="inline-flex flex-wrap items-center gap-x-[calc(6*var(--ac-u))] text-foreground-muted">
        {startDate ? (
          <RangeText
            start={startDate}
            end={parseDateValue(end)}
            allDay={allDay}
          />
        ) : (
          [start, end].filter(Boolean).join(' – ')
        )}
      </s>
      <span className="shrink-0 text-foreground-muted" aria-hidden="true">
        →
      </span>
    </>
  );
};

const EndRow = ({
  label,
  date,
  allDay,
  pressed,
  onPress,
  onTime,
}: {
  label: string;
  date: Date;
  allDay: boolean;
  pressed: boolean;
  onPress: () => void;
  onTime: (time: string) => void;
}) => (
  <XStack className="items-center gap-2">
    <span aria-hidden="true" className="w-10 text-xs text-foreground-muted">
      {label}
    </span>
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={`${label}: ${dayLabel(date)}`}
      className={END_BUTTON_CLASS}
      onClick={onPress}
    >
      {dayLabel(date)}
    </button>
    {allDay ? null : (
      <TimePickerPopover
        selectedTime={format(date, 'HH:mm')}
        triggerAriaLabel={`${label === 'Starts' ? 'Start' : 'End'} time`}
        triggerClassName="h-6 w-auto px-2 text-xs"
        onTimeSelected={onTime}
      />
    )}
  </XStack>
);

// Shown in the user's own timezone: the start and end carry their offset, and the
// event's `timezone` names where it was scheduled, so a moved start or end takes that
// zone's offset at its new instant. Moving the start moves the end with it, keeping
// the event's length; `checks` run against the end, where "Ends before it starts" lives
export const DateRange: ComponentFn<typeof catalog, 'DateRange'> = ({
  props,
  bindings,
}) => {
  const id = useElementId(props);
  const metrics = useCardMetrics();
  const startPath = bindings?.start;
  const endPath = bindings?.end;
  const [start, setStart] = useBoundProp<string>(props.start, startPath);
  const [end, setEnd] = useBoundProp<string>(props.end, endPath);
  const checks = useFieldChecks({
    path: endPath ?? startPath,
    checks: props.checks,
    validateOn: props.validateOn,
    serverError: props.error,
  });
  const session = useCardSession();
  const status = useCardStatus();
  const controls = useCardSessionControls();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<RangeEnd>('start');

  const startDate = parseDateValue(start);
  if (!startDate) return null;
  const endDate = parseDateValue(end) ?? startDate;
  const startValue = start ?? '';
  const endValue = end ?? startValue;

  const held = status === 'running';
  const editable =
    props.editable === true &&
    startPath !== undefined &&
    endPath !== undefined &&
    !session.readOnly &&
    !held;
  const allDay = props.allDay ?? isDateOnly(startValue);
  const error = checks.error ?? props.error;
  const appearance = props.appearance ?? 'text';

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) setEditing('start');
    if (!startPath) return;
    if (next) {
      controls.focus(startPath);
    } else {
      controls.blur(startPath);
      checks.onBlur();
    }
  };

  const moveStart = (nextStart: Date) => {
    const nextEnd = shiftEnd({
      start: startDate,
      end: endDate,
      nextStart,
      allDay,
    });
    setStart(writeDateValue(nextStart, startValue, props.timezone));
    setEnd(writeDateValue(nextEnd, endValue, props.timezone));
    checks.afterChange();
    controls.commitNow();
  };

  const moveEnd = (nextEnd: Date) => {
    setEnd(writeDateValue(nextEnd, endValue, props.timezone));
    checks.afterChange();
    controls.commitNow();
  };

  const pickDay = (day: Date) => {
    if (editing === 'start') moveStart(withDayOf(startDate, day));
    else moveEnd(withDayOf(endDate, day));
  };

  const shown = (
    <>
      <Icons.calendar
        className={cn(
          ICON_CLASS,
          'mr-[calc(2*var(--ac-u))] shrink-0 text-foreground-muted'
        )}
        aria-hidden="true"
      />
      {props.previousStart ? (
        <PreviousRange
          start={props.previousStart}
          end={props.previousEnd}
          allDay={allDay}
        />
      ) : null}
      <RangeText start={startDate} end={parseDateValue(end)} allDay={allDay} />
      {props.showTimezone &&
      !allDay &&
      props.timezone !== Intl.DateTimeFormat().resolvedOptions().timeZone ? (
        <span className="text-foreground-muted">
          (<Time date={startDate} timeZone={props.timezone} />{' '}
          {props.timezoneLabel ??
            props.timezone.split('/').at(-1)?.replaceAll('_', ' ')}
          )
        </span>
      ) : null}
    </>
  );
  // A pill is one line that ends in an ellipsis rather than growing past its row
  const shownClass =
    appearance === 'pill'
      ? cn(
          PILL_CLASS,
          'overflow-hidden text-ellipsis whitespace-nowrap',
          error && PILL_ERROR_CLASS
        )
      : cn(TEXT_CLASS, error && 'text-danger-11');

  let control: ReactNode;
  if (!editable) {
    control = (
      <span
        className={cn(
          shownClass,
          appearance === 'pill' && 'hover:bg-transparent',
          held && HELD_CLASS
        )}
      >
        {shown}
      </span>
    );
  } else {
    control = (
      <DateRangePicker
        mobile={metrics.mobile}
        open={open}
        onOpenChange={changeOpen}
        trigger={
          <button
            type="button"
            id={id}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? fieldErrorId(id) : undefined}
            className={cn(
              shownClass,
              appearance === 'text' && TEXT_TRIGGER_CLASS
            )}
          >
            {shown}
          </button>
        }
      >
        <YStack className="gap-1.5">
          <EndRow
            label="Starts"
            date={startDate}
            allDay={allDay}
            pressed={editing === 'start'}
            onPress={() => setEditing('start')}
            onTime={(time) => moveStart(withTimeOf(startDate, time))}
          />
          <EndRow
            label="Ends"
            date={endDate}
            allDay={allDay}
            pressed={editing === 'end'}
            onPress={() => setEditing('end')}
            onTime={(time) => moveEnd(withTimeOf(endDate, time))}
          />
          <Calendar
            mode="range"
            selected={{ from: startDate, to: endDate }}
            defaultMonth={editing === 'start' ? startDate : endDate}
            onSelect={(_range, day) => pickDay(day)}
            className="border-t border-border pt-2"
          />
        </YStack>
      </DateRangePicker>
    );
  }

  return (
    <YStack
      className={cn(
        'min-w-0 items-start gap-[calc(2*var(--ac-u))]',
        sizingClass(props.sizing)
      )}
    >
      {appearance === 'pill' ? (
        <PillField id={id} error={error}>
          {control}
        </PillField>
      ) : (
        <>
          {control}
          <FieldError id={id} error={error} />
        </>
      )}
      {props.warning ? (
        <p className={cn(TEXT_STYLE.meta, TONE_CLASS.warn)}>
          {typeof props.warning === 'string' ? (
            props.warning
          ) : (
            <>
              {props.warning.label} · <Time date={props.warning.at} />
            </>
          )}
        </p>
      ) : null}
    </YStack>
  );
};
